import {
  CopyButton,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import type { DISTANCE_AND_MIDPOINT_CALCULATOR_STRINGS } from "@/components/tools/distance-and-midpoint-calculator";
import { SegmentPlot } from "@/components/tools/linear-algebra/segment-plot";
import { t } from "@/lib/i18n/interpolate";
import {
  differences,
  distance,
  endpointFromMidpoint,
  externalSide,
  midpoint,
  type Point,
  sectionPoint,
  squaredDistance,
} from "@/lib/tool-kit/linear-algebra/analytic";
import {
  formatQuad,
  quadToComplex,
} from "@/lib/tool-kit/linear-algebra/quad-number";
import { radicalNote } from "@/lib/tool-kit/linear-algebra/right-triangle";
import {
  formatStyled,
  isZeroR,
  mulR,
  type NumberStyle,
  type Rational,
  toNumberR,
} from "@/lib/tool-kit/math/rational";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

type Strings = typeof DISTANCE_AND_MIDPOINT_CALCULATOR_STRINGS;

export interface DistanceCtx {
  strings: Strings;
  style: NumberStyle;
  mark: string;
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string;
}

const NEEDS_BRACKETS = /^−|\//;
const AXES = ["x", "y", "z"] as const;

function val(ctx: DistanceCtx, x: Rational): string {
  return formatStyled(x, ctx.style, ctx.mark);
}

function br(ctx: DistanceCtx, x: Rational): string {
  const text = val(ctx, x);
  return NEEDS_BRACKETS.test(text) ? `(${text})` : text;
}

function pointText(ctx: DistanceCtx, p: Point): string {
  return `(${p.map((x) => val(ctx, x)).join(", ")})`;
}

function exactAndDecimal(
  ctx: DistanceCtx,
  quad: ReturnType<typeof distance>
): string {
  const exact = formatQuad(quad, ctx.style, ctx.mark);
  return isZeroR(quad.b)
    ? exact
    : `${exact} ≈ ${ctx.fmt(quadToComplex(quad).re, { maximumFractionDigits: 6 })}`;
}

function Plot({
  p,
  q,
  extras,
  ctx,
}: {
  p: Point;
  q: Point;
  extras: { point: Point; label: string; color: string }[];
  ctx: DistanceCtx;
}) {
  const { strings } = ctx;
  const xy = (pt: Point, label: string, color: string) => ({
    x: toNumberR(pt[0]),
    y: toNumberR(pt[1]),
    label,
    color,
  });
  const text = (pt: Point, name: string) =>
    `${name} ${t(strings.pointText, { x: val(ctx, pt[0]), y: val(ctx, pt[1]) })}`;
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.plotHeading}</h3>
      <SegmentPlot
        downloadLabel={strings.downloadPng}
        extras={extras.map((e) => xy(e.point, text(e.point, e.label), e.color))}
        fileName="segment.png"
        from={xy(p, text(p, "P₁"), CHART_COLORS[3])}
        title={strings.plotTitle}
        to={xy(q, text(q, "P₂"), CHART_COLORS[3])}
      />
    </>
  );
}

function DistanceWorking({
  p,
  q,
  ctx,
}: {
  p: Point;
  q: Point;
  ctx: DistanceCtx;
}) {
  const { strings } = ctx;
  const d = differences(p, q);
  const squares = d.map((x) => `${br(ctx, x)}²`).join(" + ");
  const sum = squaredDistance(p, q);
  const parts = d.map((x) => val(ctx, mulR(x, x))).join(" + ");
  const quad = distance(p, q);
  let note = "";
  if (sum.den === BigInt(1) && !isZeroR(quad.b)) {
    const info = radicalNote(sum.num);
    if (info.kind === "simplified") {
      note = t(strings.noteSimplified, {
        n: sum.num.toString(),
        square: (info.coefficient * info.coefficient).toString(),
        rad: info.radicand.toString(),
        coef: info.coefficient.toString(),
      });
    } else if (info.kind === "simplest") {
      note = t(strings.noteSimplest, { n: sum.num.toString() });
    }
  }
  const terms = p
    .map((x, i) => `(${br(ctx, q[i])} − ${br(ctx, x)})²`)
    .join(" + ");
  return (
    <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
      <p>
        {t(strings.distanceFormula, {
          axes: AXES.slice(0, p.length).join(", "),
        })}
      </p>
      <p>
        {t(strings.distanceWorking, {
          terms,
          squares,
          parts,
          sum: val(ctx, sum),
          result: exactAndDecimal(ctx, quad),
        })}
      </p>
      {note === "" ? null : <p>{note}</p>}
    </div>
  );
}

function SectionBlock({
  p,
  q,
  m,
  n,
  kind,
  ctx,
}: {
  p: Point;
  q: Point;
  m: Rational;
  n: Rational;
  kind: "internal" | "external";
  ctx: DistanceCtx;
}) {
  const { strings } = ctx;
  const result = sectionPoint(p, q, m, n, kind);
  if (typeof result === "string") {
    return (
      <p className="text-muted-foreground text-sm">
        {result === "equal-external" ? strings.equalExternal : strings.badRatio}
      </p>
    );
  }
  const working =
    kind === "internal"
      ? t(strings.sectionInternal, {
          m: val(ctx, m),
          n: val(ctx, n),
          result: pointText(ctx, result),
        })
      : t(strings.sectionExternal, {
          m: val(ctx, m),
          n: val(ctx, n),
          result: pointText(ctx, result),
        });
  return (
    <>
      <StatCard
        label={t(
          kind === "internal"
            ? strings.sectionInternalLabel
            : strings.sectionExternalLabel,
          {
            m: val(ctx, m),
            n: val(ctx, n),
          }
        )}
        value={pointText(ctx, result)}
      />
      <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
        <p>
          {kind === "internal"
            ? strings.sectionInternalFormula
            : strings.sectionExternalFormula}
        </p>
        <p>{working}</p>
        {kind === "external" ? (
          <p>{strings.externalSide[externalSide(m, n)]}</p>
        ) : null}
      </div>
    </>
  );
}

export function MeasureResults({
  p,
  q,
  ratio,
  ctx,
}: {
  p: Point;
  q: Point;
  ratio: { m: Rational; n: Rational; kind: "internal" | "external" } | null;
  ctx: DistanceCtx;
}) {
  const { strings } = ctx;
  const mid = midpoint(p, q);
  const quad = distance(p, q);
  const section = ratio
    ? sectionPoint(p, q, ratio.m, ratio.n, ratio.kind)
    : null;
  const extras: { point: Point; label: string; color: string }[] = [
    { point: mid, label: "M", color: CHART_COLORS[2] },
  ];
  if (section && typeof section !== "string") {
    extras.push({ point: section, label: "S", color: CHART_COLORS[1] });
  }
  const midWorking = p
    .map((x, i) => `(${br(ctx, x)} + ${br(ctx, q[i])}) / 2`)
    .join(", ");
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.distanceLabel}
          value={exactAndDecimal(ctx, quad)}
        />
        <StatCard
          emphasis
          label={strings.midpointLabel}
          value={pointText(ctx, mid)}
        />
        <StatCard
          label={strings.squaredLabel}
          value={val(ctx, squaredDistance(p, q))}
        />
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResults}
        small
        text={`${strings.distanceLabel}: ${exactAndDecimal(ctx, quad)}\n${strings.midpointLabel}: ${pointText(ctx, mid)}`}
      />
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <DistanceWorking ctx={ctx} p={p} q={q} />
      <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
        <p>{strings.midpointFormula}</p>
        <p>
          {t(strings.midpointWorking, {
            working: midWorking,
            result: pointText(ctx, mid),
          })}
        </p>
      </div>
      {ratio ? (
        <>
          <h3 className={WIDGET_H3}>{strings.sectionHeading}</h3>
          <SectionBlock
            ctx={ctx}
            kind={ratio.kind}
            m={ratio.m}
            n={ratio.n}
            p={p}
            q={q}
          />
        </>
      ) : null}
      {p.length === 2 ? (
        <Plot ctx={ctx} extras={extras} p={p} q={q} />
      ) : (
        <p className="text-muted-foreground text-sm">{strings.noPlot3d}</p>
      )}
    </>
  );
}

export function EndpointResults({
  p,
  m,
  ctx,
}: {
  p: Point;
  m: Point;
  ctx: DistanceCtx;
}) {
  const { strings } = ctx;
  const q = endpointFromMidpoint(p, m);
  const working = p
    .map(
      (x, i) =>
        `${AXES[i]}₂ = 2 × ${br(ctx, m[i])} − ${br(ctx, x)} = ${val(ctx, q[i])}`
    )
    .join("\n");
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.endpointLabel}
          value={pointText(ctx, q)}
        />
        <StatCard
          label={strings.distanceLabel}
          value={exactAndDecimal(ctx, distance(p, q))}
        />
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResults}
        small
        text={pointText(ctx, q)}
      />
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
        <p>{strings.endpointFormula}</p>
        {working.split("\n").map((line) => (
          <p key={line}>{line}</p>
        ))}
      </div>
      {p.length === 2 ? (
        <Plot
          ctx={ctx}
          extras={[{ point: m, label: "M", color: CHART_COLORS[2] }]}
          p={p}
          q={q}
        />
      ) : (
        <p className="text-muted-foreground text-sm">{strings.noPlot3d}</p>
      )}
    </>
  );
}
